class VideoGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-vg-track]');
    this.cards = Array.from(this.querySelectorAll('[data-vg-card]'));
    this.videos = Array.from(this.querySelectorAll('[data-vg-video]'));
    if (!this.track || !this.cards.length) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !reduceMotion;

    this.setupAutoplay();
    this.setupArrows();
    this.setupDots();
    this.setupModal();
  }

  disconnectedCallback() {
    this.playIO?.disconnect();
    this.dotIO?.disconnect();
  }

  setupAutoplay() {
    if (!this.autoplay || !('IntersectionObserver' in window)) return;
    this.playIO = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) { v.play().catch(() => {}); } else { v.pause(); }
      });
    }, { root: this.track, threshold: 0.5 });
    this.videos.forEach((v) => this.playIO.observe(v));
  }

  cardStep() { return (this.cards[0]?.offsetWidth || 240) + parseInt(getComputedStyle(this.track).columnGap || '16', 10); }

  setupArrows() {
    this.querySelector('[data-vg-prev]')?.addEventListener('click', () => this.track.scrollBy({ left: -this.cardStep(), behavior: 'smooth' }));
    this.querySelector('[data-vg-next]')?.addEventListener('click', () => this.track.scrollBy({ left: this.cardStep(), behavior: 'smooth' }));
  }

  setupDots() {
    this.dots = Array.from(this.querySelectorAll('[data-vg-dot]'));
    if (!this.dots.length || !('IntersectionObserver' in window)) return;
    this.dotIO = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const i = this.cards.indexOf(e.target);
        this.dots.forEach((d, di) => d.setAttribute('aria-current', di === i ? 'true' : 'false'));
      });
    }, { root: this.track, threshold: 0.6 });
    this.cards.forEach((c) => this.dotIO.observe(c));
    this.dots.forEach((d, i) => d.addEventListener('click', () => {
      this.cards[i]?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }));
  }

  setupModal() {
    this.modal = this.querySelector('[data-vg-modal]');
    this.modalVideo = this.modal?.querySelector('[data-vg-modal-video]');
    this.modalChip = this.modal?.querySelector('[data-vg-modal-chip]');
    if (!this.modal || !this.modalVideo) return;

    this.cards.forEach((card, i) => {
      const opener = card.querySelector('[data-vg-open]');
      if (!opener) return;
      opener.addEventListener('click', () => this.openModal(i));
      opener.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); this.openModal(i); }
      });
      card.querySelector('[data-vg-chip]')?.addEventListener('click', (ev) => ev.stopPropagation());
    });

    this.modal.querySelector('[data-vg-close]')?.addEventListener('click', () => this.modal.close());
    this.modal.querySelector('[data-vg-modal-prev]')?.addEventListener('click', () => this.openModal(this.current - 1));
    this.modal.querySelector('[data-vg-modal-next]')?.addEventListener('click', () => this.openModal(this.current + 1));
    this.modal.addEventListener('click', (ev) => { if (ev.target === this.modal) this.modal.close(); });
    this.modalVideo.addEventListener('click', () => { this.modalVideo.paused ? this.modalVideo.play() : this.modalVideo.pause(); });
    this.modal.addEventListener('close', () => {
      this.modalVideo.pause();
      this.modalVideo.innerHTML = '';
      this.modalVideo.load();
      this.resumePreviews();
    });
  }

  openModal(index) {
    const n = this.cards.length;
    this.current = (index + n) % n;
    const card = this.cards[this.current];
    const preview = card.querySelector('[data-vg-video]');
    const chip = card.querySelector('[data-vg-chip]');

    // Previews keep playing (muted) behind the modal; only the modal has audio.
    this.modalVideo.innerHTML = preview ? preview.innerHTML : '';
    this.modalVideo.muted = false;
    this.modalVideo.load();
    this.modalVideo.play().catch(() => {});
    if (this.modalChip) this.modalChip.innerHTML = chip ? chip.outerHTML : '';

    if (!this.modal.open) this.modal.showModal();
  }

  resumePreviews() {
    if (!this.autoplay) return;
    this.videos.forEach((v) => { v.muted = true; });
    // visible previews se reanudan vía el IntersectionObserver existente
  }
}
customElements.define('video-gallery', VideoGallery);
